import React, { useEffect, useRef, useState } from 'react';
import classNames from 'clsx';
import type { IMenuItemProps, MenuPreviewStateMap } from '../../../Menu.types';
import { MenuItemContext } from '../../../../../common/menu/MenuItemContext';
import { createEventListeners } from './utils';
import { showDropdown } from './showDropdown';
import classes from './style/MenuItem.scss';
import {
  ANIMATION_CONFIG_MAP,
  AnimationParts,
  INITIAL_ANIMATION_STATE,
  testIds,
} from '../../constants';
import { Dropdown } from '../Dropdown';
import type { MenuItemProps } from '@wix/editor-elements-definitions';
import { MenuItemLabel } from './MenuItemLabel';
import { isEmptyObject } from '@wix/editor-elements-common-utils';
import { useAnimationState } from './useAnimationState';

type HorizontalDropdownProps = {
  onEscKeyDown: () => void;
  item: MenuItemProps;
  currentItem?: MenuItemProps;
  children?: React.ReactNode;
};

type VerticalDropdownProps = {
  open: boolean;
  onEscKeyDown: () => void;
  item: MenuItemProps & {
    forceOpen?: boolean;
    children?: React.ReactNode | undefined;
  };
  currentItem?: MenuItemProps;
  partToPreviewStateMap?: MenuPreviewStateMap;
};

function HorizontalDropdown(props: HorizontalDropdownProps) {
  return (
    <MenuItemContext.Provider
      value={{
        onEscKeyDown: props.onEscKeyDown,
        item: props.item,
        currentItem: props.currentItem,
      }}
    >
      <Dropdown
        className={classes.horizontalDropdownDisplayWrapper}
        children={props.children}
        item={props.item}
        menuOrientation="horizontal"
      />
    </MenuItemContext.Provider>
  );
}

function VerticalDropdown(props: VerticalDropdownProps) {
  return (
    <MenuItemContext.Provider
      value={{
        onEscKeyDown: props.onEscKeyDown,
        item: props.item,
        currentItem: props.currentItem,
      }}
    >
      <Dropdown
        item={props.item}
        menuOrientation="vertical"
        className={classNames(
          classes.verticalDropdownDisplayWrapper,
          props.open && classes.expandedDropdown,
        )}
        partToPreviewStateMap={props.partToPreviewStateMap}
      />
    </MenuItemContext.Provider>
  );
}

export const MenuItem: React.FC<IMenuItemProps> = props => {
  const {
    item,
    currentItem,
    focusOnMenuItem,
    getAnimationName,
    hideDivider,
    partToPreviewStateMap,
  } = props;
  const { children, forceOpen = false } = item;
  const [isOpen, setIsOpen] = useState(forceOpen);
  const { animationState, initEnterAnimation, initExitAnimation } =
    useAnimationState(ANIMATION_CONFIG_MAP, INITIAL_ANIMATION_STATE);
  const hasSubItems = !!item.items?.length;
  const hasMegaMenuContainer = !!children;
  const hasDropdownMenu = hasMegaMenuContainer || hasSubItems;
  const hasLink = !isEmptyObject(item.link);
  const hasLinkOrSubItems = hasLink || hasSubItems;

  const chevronButtonRef = useRef<HTMLButtonElement>(null);

  const itemRef = useRef<HTMLLIElement>(null);

  const eventListeners = createEventListeners({
    showDropdown: () => setIsOpen(true),
    hideDropdown: () => setIsOpen(false),
    itemRef,
    getAnimationName,
    initEnterAnimation,
    initExitAnimation,
    isOpen,
    hasLink,
  });

  useEffect(() => {
    if (currentItem?.id === item.id && itemRef.current) {
      focusOnMenuItem?.(itemRef.current);
    }
  }, [currentItem, item, focusOnMenuItem]);

  useEffect(() => {
    if (!isOpen || !itemRef.current) {
      return;
    }

    return showDropdown(itemRef.current);
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(!!forceOpen);
  }, [forceOpen]);

  const handleEscKeyDown = () => {
    chevronButtonRef.current?.focus();

    setIsOpen(false);
  };

  return (
    <li
      ref={itemRef}
      className={classNames(classes.listItem, {
        [classes.visibleOnVerticalMode]: hasLinkOrSubItems,
      })}
      data-testid={testIds.menuItem}
      data-animation-part={AnimationParts.MenuItem}
      data-animation-name={animationState.name}
      data-animation-state={animationState.phase}
      data-item-depth="0" // For scrolling, to know how much items on depth=0
    >
      <div className={classes.itemWrapper} {...eventListeners}>
        <MenuItemLabel
          {...props}
          hasDropdownMenu={hasDropdownMenu}
          hasSubItems={hasSubItems}
          chevronButtonRef={chevronButtonRef}
          setIsOpen={setIsOpen}
          isOpen={isOpen}
        />
        {hasDropdownMenu && (
          <HorizontalDropdown
            onEscKeyDown={handleEscKeyDown}
            item={item}
            currentItem={currentItem}
            children={children}
          />
        )}
      </div>
      {hasSubItems && (
        <VerticalDropdown
          open={isOpen}
          onEscKeyDown={handleEscKeyDown}
          item={item}
          currentItem={currentItem}
          partToPreviewStateMap={partToPreviewStateMap}
        />
      )}
      <span
        className={classNames(classes.divider, {
          [classes.hideDivider]: hideDivider,
        })}
      ></span>
    </li>
  );
};
